import { useEffect, useState } from 'react';
import { api, dateTime } from '../api.js';
import { useAuth } from '../App.jsx';

/**
 * Фискални устройства и работни места.
 *
 * Приложение № 29 иска и двете описани: т. 18.9 ги изброява като
 * номенклатури, а т. 8 иска готовността на устройството да се знае в
 * реално време и при липса да се блокира откриването на продажби за
 * конкретното работно място.
 *
 * Индивидуалният номер на устройството не се променя. УНП-тата, издадени
 * с него, го носят завинаги; смяната му би пренаписала историята. Сменен
 * апарат се описва като нов, а старият се спира.
 */
export default function FiscalDevices() {
  const { user } = useAuth();
  const admin = user.role === 'admin';

  const [devices, setDevices] = useState([]);
  const [stations, setStations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [error, setError] = useState(null);
  const [vest, setVest] = useState(null);
  const [nov, setNov] = useState(null);
  const [novoMyasto, setNovoMyasto] = useState(null);
  const [az, setAz] = useState(null);

  async function zaredi() {
    try {
      const [d, s, w, r] = await Promise.all([
        api.get('/api/fiscal-devices'),
        api.get('/api/workstations'),
        api.get('/api/warehouses'),
        api.get('/api/fiscal-readiness'),
      ]);
      setDevices(d); setStations(s); setWarehouses(w); setAz(r);
    } catch (e) { setError(e.message); }
  }

  useEffect(() => { zaredi(); }, []);

  async function zapishiUstroystvo() {
    try {
      await api.post('/api/fiscal-devices', {
        serial: nov.serial.trim(),
        model: nov.model.trim() || null,
        fiscalMemory: nov.memory.trim() || null,
      });
      setNov(null);
      setVest('Устройството е описано');
      await zaredi();
    } catch (e) { setError(e.message); }
  }

  async function smeni(id, patch) {
    try {
      await api.patch(`/api/fiscal-devices/${id}`, patch);
      await zaredi();
    } catch (e) { setError(e.message); }
  }

  async function smeniMyasto(id, patch) {
    try {
      await api.patch(`/api/workstations/${id}`, patch);
      await zaredi();
    } catch (e) { setError(e.message); }
  }

  async function zapishiMyasto() {
    try {
      await api.post('/api/workstations', {
        code: novoMyasto.code.trim(),
        name: novoMyasto.name.trim(),
        ipAddress: novoMyasto.ip.trim() || null,
        warehouseId: novoMyasto.warehouseId ? Number(novoMyasto.warehouseId) : null,
        fiscalDeviceId: novoMyasto.deviceId ? Number(novoMyasto.deviceId) : null,
      });
      setNovoMyasto(null);
      setVest('Работното място е описано');
      await zaredi();
    } catch (e) { setError(e.message); }
  }

  return (
    <>
      <div className="page-head">
        <h1>Фискални устройства</h1>
        <div className="spacer" />
        {admin && !nov && (
          <button onClick={() => setNov({ serial: '', model: '', memory: '' })}>
            Закачи ново устройство
          </button>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {vest && <div className="notice">{vest}</div>}

      {/*
        * От кой адрес гледаш точно сега.
        *
        * Работното място се разпознава по него, а не по това, което
        * браузърът твърди — затова адресът се показва тук, за да се впише
        * в мястото, без да се търси другаде.
        */}
      {az && (
        <div className="card">
          <div className="card-body">
            {az.recognised ? (
              <>Влизате от <code>{az.ip}</code> — разпознато като{' '}
                <strong>{az.workstation}</strong> ({az.workstationName}).</>
            ) : (
              <>Влизате от <code>{az.ip}</code>. Този адрес не е описан като работно място,
                затова оттук се правят само справки. Продажби се откриват от машина със
                свързано фискално устройство.</>
            )}
          </div>
        </div>
      )}

      {nov && (
        <div className="card">
          <div className="card-head">Ново устройство</div>
          <div className="card-body">
            <div className="toolbar">
              <label className="field">
                Индивидуален номер
                <input value={nov.serial} autoFocus maxLength={8}
                  placeholder="осем знака"
                  onChange={(e) => setNov((n) => ({ ...n, serial: e.target.value }))} />
              </label>
              <label className="field">
                Модел
                <input value={nov.model} placeholder="напр. Datecs DP-150"
                  onChange={(e) => setNov((n) => ({ ...n, model: e.target.value }))} />
              </label>
              <label className="field">
                Фискална памет
                <input value={nov.memory}
                  onChange={(e) => setNov((n) => ({ ...n, memory: e.target.value }))} />
              </label>
              <div className="spacer" />
              <button className="primary" disabled={nov.serial.trim().length !== 8}
                onClick={zapishiUstroystvo}>Запиши</button>
              <button onClick={() => setNov(null)}>Откажи</button>
            </div>
            <p className="hint">
              Индивидуалният номер е осем знака и не се променя после — с него
              се формира УНП-то по т. 9 и всички издадени номера го носят.
            </p>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head">Устройства</div>
        <table className="compact">
          <thead>
            <tr>
              <th>Индивидуален номер</th>
              <th>Модел</th>
              <th>Фискална памет</th>
              <th>Работни места</th>
              <th>Последна връзка</th>
              <th>Час на ФУ</th>
              <th>Състояние</th>
              {admin && <th />}
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id} className={Number(d.is_active) ? '' : 'muted'}>
                <td><strong>{d.serial}</strong></td>
                <td>{d.model ?? '—'}</td>
                <td className="muted">{d.fiscal_memory ?? '—'}</td>
                <td className="num">{d.mesta}</td>
                <td className={d.last_seen_at ? '' : 'muted'}>
                  {d.last_seen_at ? dateTime(d.last_seen_at) : 'никога'}
                </td>
                <td className={d.last_time_check_at ? '' : 'muted'}>
                  {/* Т. 5: синхронизация на часа, проверявана от агента
                      при стартиране/поне веднъж на работен ден. */}
                  {d.last_time_check_at
                    ? (
                      <>
                        {dateTime(d.last_time_check_at)}{' '}
                        {d.last_time_drift_seconds === null ? (
                          <span className="badge muted">неразчетен</span>
                        ) : Math.abs(d.last_time_drift_seconds) > 60 ? (
                          <span className="badge danger">
                            разлика {d.last_time_drift_seconds > 0 ? '+' : ''}
                            {d.last_time_drift_seconds}с
                          </span>
                        ) : (
                          <span className="badge ok">сверен</span>
                        )}
                      </>
                    )
                    : 'никога'}
                </td>
                <td>
                  <span className={`badge ${Number(d.is_active) ? 'ok' : 'muted'}`}>
                    {Number(d.is_active) ? 'действащо' : 'спряно'}
                  </span>
                </td>
                {admin && (
                  <td>
                    <button className="link"
                      onClick={() => smeni(d.id, { isActive: !Number(d.is_active) })}>
                      {Number(d.is_active) ? 'спри' : 'върни'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {devices.length === 0 && (
              <tr><td colSpan={7} className="empty">Няма описано устройство</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="page-head" style={{ marginTop: 24 }}>
        <h2 style={{ margin: 0 }}>Работни места</h2>
        <div className="spacer" />
        {admin && !novoMyasto && (
          <button onClick={() => setNovoMyasto({ code: '', name: '', ip: '', warehouseId: '', deviceId: '' })}>
            Ново работно място
          </button>
        )}
      </div>

      {novoMyasto && (
        <div className="card">
          <div className="card-body">
            <div className="toolbar">
              <label className="field">
                Код
                <input value={novoMyasto.code} autoFocus placeholder="POS03"
                  onChange={(e) => setNovoMyasto((m) => ({ ...m, code: e.target.value }))} />
              </label>
              <label className="field">
                Наименование
                <input value={novoMyasto.name}
                  onChange={(e) => setNovoMyasto((m) => ({ ...m, name: e.target.value }))} />
              </label>
              <label className="field">
                Адрес в мрежата
                <input value={novoMyasto.ip} placeholder="192.168.0.20"
                  onChange={(e) => setNovoMyasto((m) => ({ ...m, ip: e.target.value }))} />
              </label>
              <label className="field">
                Търговски обект
                <select value={novoMyasto.warehouseId}
                  onChange={(e) => setNovoMyasto((m) => ({ ...m, warehouseId: e.target.value }))}>
                  <option value="">—</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </label>
              <label className="field">
                Свързано устройство
                <select value={novoMyasto.deviceId}
                  onChange={(e) => setNovoMyasto((m) => ({ ...m, deviceId: e.target.value }))}>
                  <option value="">—</option>
                  {devices.filter((d) => Number(d.is_active))
                    .map((d) => <option key={d.id} value={d.id}>{d.serial}</option>)}
                </select>
              </label>
              <div className="spacer" />
              <button className="primary"
                disabled={!novoMyasto.code.trim() || !novoMyasto.name.trim()}
                onClick={zapishiMyasto}>Запиши</button>
              <button onClick={() => setNovoMyasto(null)}>Откажи</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <table className="compact">
          <thead>
            <tr>
              <th>Код</th>
              <th>Наименование</th>
              <th>Адрес в мрежата</th>
              <th>Търговски обект</th>
              <th>Свързано ФУ</th>
              <th>Готовност</th>
              {admin && <th />}
            </tr>
          </thead>
          <tbody>
            {stations.map((s) => (
              <tr key={s.id} className={Number(s.is_active) ? '' : 'muted'}>
                <td><strong>{s.code}</strong></td>
                <td>{s.name}</td>
                <td>
                  {s.ip_address
                    ? <code>{s.ip_address}</code>
                    : <span className="error">не е зададен</span>}
                </td>
                <td className="muted">{s.warehouse_name ?? '—'}</td>
                <td>{s.device_serial ?? <span className="error">няма</span>}</td>
                <td>
                  {/*
                    * Т. 8: допуска се УНП да ползва номера на устройство, с
                    * което е имало връзка през предходните два часа. След
                    * това откриването на продажби се блокира.
                    */}
                  {!s.device_serial
                    ? <span className="muted">—</span>
                    : Number(s.gotovo)
                      ? <span className="badge ok">връзка има</span>
                      : <span className="badge danger">няма връзка от 2 часа</span>}
                </td>
                {admin && (
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="link" onClick={() => {
                      const nov = window.prompt(
                        `Адрес на ${s.code} в локалната мрежа.\n\n`
                        + 'По него сървърът разпознава машината. Запазете го в рутера, '
                        + 'за да не се размества.',
                        s.ip_address ?? az?.ip ?? '');
                      if (nov !== null) smeniMyasto(s.id, { ipAddress: nov.trim() || null });
                    }}>адрес</button>
                    <button className="link" style={{ marginLeft: 8 }}
                      onClick={() => smeniMyasto(s.id, { isActive: !Number(s.is_active) })}>
                      {Number(s.is_active) ? 'спри' : 'върни'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {stations.length === 0 && (
              <tr><td colSpan={7} className="empty">Няма описано работно място</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="hint">
        Готовността се обновява от местната услуга, която държи апарата на
        кабела — тя се обажда насам при всяка успешна връзка. Докато такава
        услуга няма, колоната ще стои празна.
      </p>
      <p className="hint">
        <strong>Работното място се разпознава по адреса в мрежата.</strong> За да
        работи, машините трябва да влизат по локалния адрес на сървъра
        (<code>192.168.0.136</code>), а не по публичното име: при второто
        трафикът излиза до рутера и се връща, и сървърът вижда рутера вместо
        машината. Адресите се запазват в рутера, за да не се разместват.
      </p>
    </>
  );
}
